import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container,
} from "@mui/material";

import PieCard from "../Components/Cards/Pie";
import BarCharts from "../Components/Cards/Bar_Charts";
import UsersCard from "../Components/Cards/Useres_Card";

import HomeIcon from "./../Assets/Icons/home.svg";
import UsersIcon from "./../Assets/Icons/Users.svg";

import { fetchStatistics } from "../Redux/statisticsSlice";

const Statistiken = () => {
  const dispatch = useDispatch();
  const [timeRange, setTimeRange] = useState("this week");
  const [showDemo, setShowDemo] = useState(false);
  
  const {
    reservationsNumber,
    usersNumber,
    popularPackages,
    profit,
    dailyBookings,
    isLoading,
    error,
  } = useSelector((state) => state.statistics);

  // Demo data for when API fails
  const demoData = {
    reservationsNumber: 45,
    usersNumber: 23,
    popularPackages: [
      { name: "Wellness Paket", count: 15 },
      { name: "Massage Paket", count: 12 },
      { name: "Sauna Paket", count: 8 },
      { name: "Spa Paket", count: 6 },
      { name: "Relax Paket", count: 4 },
    ],
    profit: [{ total: 2850 }],
    dailyBookings: [
      { _id: "2024-01-15", count: 8 },
      { _id: "2024-01-16", count: 12 },
      { _id: "2024-01-17", count: 6 },
      { _id: "2024-01-18", count: 15 },
      { _id: "2024-01-19", count: 9 },
    ],
  };

  // Use demo data if showDemo is true
  const displayData = showDemo ? demoData : {
    reservationsNumber,
    usersNumber,
    popularPackages,
    profit,
    dailyBookings,
  };

  const getDateFromTimeRange = (range) => {
    const now = new Date();
    const forDate = new Date(now);
    
    forDate.setHours(0, 0, 0, 0);

    if (range === "this day") {
      return forDate.toISOString();
    } else if (range === "this week") {
      forDate.setDate(now.getDate() - 7);
      return forDate.toISOString();
    } else if (range === "this month") {
      forDate.setMonth(now.getMonth() - 1);
      return forDate.toISOString();
    }
    return forDate.toISOString();
  };

  useEffect(() => {
    const forUsers = getDateFromTimeRange(timeRange);
    const forReservation = getDateFromTimeRange(timeRange);

    dispatch(
      fetchStatistics({
        forUsers,
        forReservation,
      })
    );
  }, [dispatch, timeRange]);

  const pieData = Array.isArray(displayData.popularPackages)
    ? displayData.popularPackages.map((item) => ({
        label: item.name || "Unbekannt",
        value: item.count || 0,
      }))
    : [];

  const barChartData = {
    profit: Array.isArray(displayData.profit) && displayData.profit.length > 0 ? displayData.profit[0]?.total || 0 : 0,
    dailyBookings: Array.isArray(displayData.dailyBookings) ? displayData.dailyBookings : [],
  };

  const cardData = [
    {
      icon: HomeIcon,
      title: "Gesamtbuchungen",
      count: `${displayData.reservationsNumber || 0} Buchungen`,
    },
    {
      icon: UsersIcon,
      title: "Neue Kunden",
      count: `${displayData.usersNumber || 0} Kunden`,
    },
  ];

  const getTimeRangeLabel = (range) => {
    switch (range) {
      case "this day":
        return "Heute";
      case "this week":
        return "Diese Woche";
      case "this month":
        return "Diesen Monat";
      default:
        return "Diese Woche";
    }
  };

  const handleRetry = () => {
    const forUsers = getDateFromTimeRange(timeRange);
    const forReservation = getDateFromTimeRange(timeRange);

    dispatch(
      fetchStatistics({
        forUsers,
        forReservation,
      })
    );
  };

  const handleShowDemo = () => {
    // Show demo data when API fails
    setShowDemo(true);
  };

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <button
                onClick={handleRetry}
                style={{
                  background: "#CD9D41",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Erneut versuchen
              </button>
              <button
                onClick={handleShowDemo}
                style={{
                  background: "#6C757D",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Demo anzeigen
              </button>
            </Box>
          }
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Fehler beim Laden der Statistiken
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontSize: "12px", opacity: 0.8 }}>
            Überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            color: "#2C3E50",
            mb: 1,
          }}
        >
          Dashboard Statistiken
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: "#7F8C8D",
            mb: 2,
          }}
        >
          Übersicht über Ihre Geschäftsstatistiken für {getTimeRangeLabel(timeRange)}
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress size={60} sx={{ color: "#CD9D41" }} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Statistics Cards Row */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {cardData.map((item, index) => (
                <Grid item xs={12} sm={6} md={6} key={index}>
                  <UsersCard
                    {...item}
                    timeRange={timeRange}
                    setTimeRange={setTimeRange}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Pie Chart */}
              <Grid item xs={12} md={6} lg={5}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    minHeight: "400px",
                    borderRadius: "12px",
                    border: "2px solid #CD9D4126",
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#2C3E50",
                        mb: 2,
                      }}
                    >
                      Beliebte Pakete ({getTimeRangeLabel(timeRange)})
                    </Typography>
                    {pieData.length > 0 ? (
                      <PieCard
                        data={pieData}
                        title={`Beliebte Pakete (${getTimeRangeLabel(timeRange)})`}
                      />
                    ) : (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="300px"
                      >
                        <Typography color="textSecondary">
                          Keine Daten verfügbar
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Bar Chart */}
              <Grid item xs={12} md={6} lg={7}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    minHeight: "400px",
                    borderRadius: "12px",
                    border: "2px solid #CD9D4126",
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#2C3E50",
                        mb: 2,
                      }}
                    >
                      Gewinn & Buchungen ({getTimeRangeLabel(timeRange)})
                    </Typography>
                    <BarCharts
                      profit={barChartData.profit}
                      dailyBookings={barChartData.dailyBookings}
                      title="Statistiken"
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Summary Row */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "12px",
                border: "2px solid #CD9D4126",
                bgcolor: "#FEFEFE",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#2C3E50",
                  mb: 2,
                }}
              >
                Zusammenfassung
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#CD9D41",
                        mb: 1,
                      }}
                    >
                      {displayData.reservationsNumber || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Gesamtbuchungen
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#CD9D41",
                        mb: 1,
                      }}
                    >
                      {displayData.usersNumber || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Neue Kunden
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#CD9D41",
                        mb: 1,
                      }}
                    >
                      €{barChartData.profit.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Gesamtgewinn
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#CD9D41",
                        mb: 1,
                      }}
                    >
                      {pieData.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Aktive Pakete
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Statistiken;
